import { useEffect, useRef, useState, useCallback } from 'react';

export default function QuizTimer({ totalSeconds, onTimeout, resetKey }) {
  const [timeLeft, setTimeLeft] = useState(totalSeconds);
  const startRef = useRef(Date.now());
  const frameRef = useRef(null);
  const calledRef = useRef(false);

  const tick = useCallback(() => {
    const elapsed = (Date.now() - startRef.current) / 1000;
    const remaining = Math.max(0, totalSeconds - elapsed);
    setTimeLeft(remaining);
    if (remaining <= 0 && !calledRef.current) {
      calledRef.current = true;
      onTimeout();
      return;
    }
    frameRef.current = requestAnimationFrame(tick);
  }, [totalSeconds, onTimeout]);

  useEffect(() => {
    calledRef.current = false;
    startRef.current = Date.now();
    setTimeLeft(totalSeconds);
    frameRef.current = requestAnimationFrame(tick);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [resetKey, totalSeconds, tick]);

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const progress = timeLeft / totalSeconds;
  const offset = circumference * (1 - progress);
  const displayTime = Math.ceil(timeLeft);
  const isWarning = displayTime <= 5;

  let strokeColor = '#10B981';
  if (displayTime <= 5) strokeColor = '#EF4444';
  else if (displayTime <= 10) strokeColor = '#F59E0B';

  return (
    <div className={`relative w-14 sm:w-20 h-14 sm:h-20 ${isWarning ? 'timer-warning' : ''}`} data-testid="quiz-timer">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={radius} stroke="#1C1C1F" strokeWidth="6" fill="none" />
        <circle
          cx="50" cy="50" r={radius}
          stroke={strokeColor}
          strokeWidth="6" fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke 0.3s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`font-outfit font-bold text-lg sm:text-2xl ${isWarning ? 'text-red-400' : 'text-white'}`}>
          {displayTime}
        </span>
      </div>
    </div>
  );
}
